import {DiseaseActions} from '../../models/diseases';
import {cloneDeep} from 'lodash';
import {AuthState} from 'src/app/models/auth/auth.redux';
import {IUser} from 'src/app/models/auth/user.interface';
import {ServiceAction, ServiceState} from '../../models/service';
import {IRecordEmit, IRecordOption} from '../../interfaces/record';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {Logger} from '../../class/helper/logger.class';
import {Component, OnDestroy, OnInit} from '@angular/core';
import {Loader} from '../../models/loader/loader.redux';
import {GenderEnum} from '../../models/user';
import {Store} from '@ngxs/store';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {IPatient, PatientState} from '../../models/patient';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {take} from 'rxjs/operators';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {RecordHistoryAttitudeAction} from '../../models/record-history-attitude';
import {HttpParams} from '@angular/common/http';
import {RecordTypeEnum} from '../../enums/record';
import {QueryParamsService} from '../../services/query-params/query-params.service';
import {RecordEnvironmentAffectivityAction} from '../../models/record-environment-affectivity';
import {RecordBiochemistAction} from '../../models/record-history-biochemists';
import {RecordMedicalInheritedFamilyAction} from '../../models/record-medical-inherited-family';
import {RecordMedicalPersonalAction} from '../../models/record-medical-personal';
import {RecordNutritionalHistoryAction} from '../../models/record-nutritional-history';
import {RecordNutritionalPhysicalExaminationAction} from '../../models/record-nutritional-physical-examination';
import {RecordOtherHabitsAction} from '../../models/record-other-habits';
import {RecordPhysicalActivityAction} from '../../models/record-physical-activity';
import {RecordSleepAction} from '../../models/record-sleep';
import {RecordHistoryExperienceAction} from '../../models/record-history-my-experience';
import {SphereModeEnum} from '../../enums/sphere/sphere-mode.enum';
import {AppTourService} from '../../services/app-tour/app-tour.service';

@Component({
  selector: 'app-consultation-file',
  templateUrl: './consultation-file.component.html',
  styleUrls: ['./consultation-file.component.scss'],
  animations: [fadeInAnimation]
})
export class ConsultationFileComponent implements OnInit, OnDestroy {
  // tslint:disable:variable-name
  patient$: Observable<IPatient> = this.store.select<IPatient>(PatientState.getSelectedPatient);
  serviceId$: Observable<string> = this.store.select<string>(ServiceState.getServiceSelectedId);
  recordSelected: IRecordOption;
  records: IRecordOption[] = [];

  patientSubscription: Subscription;
  serviceSubscription: Subscription;
  patient: IPatient;
  params: Params;
  appointmentId: string;

  recordTypeEnum = RecordTypeEnum;

  queryParams: HttpParams;

  activeRecord: RecordTypeEnum;
  constructor(
    private store: Store,
    private router: Router,
    private utilitiesService: UtilitiesService,
    private activatedRoute: ActivatedRoute,
    private messageAlertService: MessageAlertService,
    private queryParamsService: QueryParamsService,
    private appTourService: AppTourService,
  ) { }

  ngOnInit(): void {
    this.initValues().then();
    this.listeners();
  }

  /**
   * Inicializa las propiedades necesarias.
   */
  async initValues(): Promise<void> {
    this.params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.appointmentId = (await firstValueFrom(this.activatedRoute.queryParams.pipe(take(1))))?.appointmentId;
  }

  /**
   * Hace las primeras peticiones necesarias al servidor.
   */
  async initValuesHttp(): Promise<void> {
    try {
      const user: IUser = cloneDeep(await firstValueFrom((this.store.selectOnce(AuthState.getUser))));
      const serviceId = await firstValueFrom((this.store.selectOnce(ServiceState.getServiceSelectedId)));
      if (!user || !user.experts || !this.params?.patientId) return;
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Cargando fichas ...'))));
      await firstValueFrom((this.store.dispatch(new ServiceAction.GetRecords(serviceId, this.params.patientId, SphereModeEnum.MINI))));
      let records: IRecordOption[] = cloneDeep(await firstValueFrom((this.store.selectOnce(ServiceState.getRecordsInformation))));
      records = records.filter(record => record.lastUpdated);
      records?.forEach(record => {
        this.records.push({
          ...record,
          hide: false
        });
      });
      // Dependiendo de la ficha leída del queryParam, abrir la ficha de inicio
      this.queryParams = new HttpParams();
      this.readAndSetQueryParams();
      const activeRecord = this.queryParams.get('active-record') as RecordTypeEnum;
      if (activeRecord) {
        await this.onActiveRecord(activeRecord);
      }
      await firstValueFrom(this.store.dispatch(new DiseaseActions.GetCategories({ include: ['diseases'] })));
      await firstValueFrom(this.store.dispatch(new RecordNutritionalPhysicalExaminationAction.GetCategories({ include: ['physicalExaminationSigns'] })));
    } catch (e) {
      Logger.log(e?.message);
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Pone los observables en escucha de cambios.
   */
  private listeners(): void {
    this.patientSubscription = this.patient$.subscribe(async (data: IPatient) => {
      if (!data) return;
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo la información...'))));
      await this.setPatient(data);
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    });

    this.serviceSubscription = this.serviceId$.subscribe(id => {
      if (!id) return;
      this.initValuesHttp().then();
    });
  }


  /**
   * Selecciona una ficha.
   * @param record Ficha
   */
  onSelectRecord(record: IRecordOption): void {
    this.recordSelected = record;
    switch (record.uid) {
      case RecordTypeEnum.HABITS:
        this.store.dispatch(new RecordOtherHabitsAction.Deploy(true));
        break;
      case RecordTypeEnum.PHYSICAL_ACTIVITY:
        this.store.dispatch(new RecordPhysicalActivityAction.Deploy(true));
        break;
      case RecordTypeEnum.PERSONAL_MEDIC:
        this.store.dispatch(new RecordMedicalPersonalAction.Deploy(true));
        break;
      case RecordTypeEnum.FAMILIAL_MEDICAL:
        this.store.dispatch(new RecordMedicalInheritedFamilyAction.Deploy(true));
        break;
      case RecordTypeEnum.NUTRITIONAL:
        this.store.dispatch(new RecordNutritionalHistoryAction.Deploy(true));
        break;
      case RecordTypeEnum.ENVIRONMENT_AFFECTIVITY:
        this.store.dispatch(new RecordEnvironmentAffectivityAction.Deploy(true));
        break;
      case RecordTypeEnum.ATTITUDE:
        this.store.dispatch(new RecordHistoryAttitudeAction.Deploy(true));
        break;
      case RecordTypeEnum.NUTRITIONAL_PHYSICAL_EXAMINATION:
        this.store.dispatch(new RecordNutritionalPhysicalExaminationAction.Deploy(true));
        break;
      case RecordTypeEnum.SLEEP:
        this.store.dispatch(new RecordSleepAction.Deploy(true));
        break;
      case RecordTypeEnum.BIOCHEMISTS:
        this.store.dispatch(new RecordBiochemistAction.Deploy(true));
        break;
      case RecordTypeEnum.MY_EXPERIENCE:
        this.store.dispatch(new RecordHistoryExperienceAction.Deploy(true));
        break;
    }
    if (!this.appTourService.tourMode) {
      this.setActiveRecordQueryParam(record.uid);
    }
  }

  /**
   * Abre por default la ficha dada
   * @param recordType enum de la ficha a abrir
   */
   async onActiveRecord(recordType: RecordTypeEnum): Promise<void> {
    const record: IRecordOption = this.records.find(_record => _record.uid === recordType);
    this.activeRecord = record.uid;
    await this.onSelectRecord(record);
  }

  /**
   * Lee los queryParams, y los setea en la variable queryParams
   */
   readAndSetQueryParams(): void {
    const queryParams = this.utilitiesService.getAllQueryParams(this.activatedRoute);
    Object.keys(queryParams).forEach(queryParam => {
      this.queryParams = this.queryParamsService.setQueryString(this.router.url, this.queryParams, queryParam, queryParams[queryParam]);
    });
  }

  /**
   * Setea la ficha activa en los query params
   * @param recordType enum para la ficha activa o null para quitar el queryParam
   */
  setActiveRecordQueryParam(recordType?: RecordTypeEnum): void {
    if (recordType) {
      this.queryParams = this.queryParamsService.setQueryString(this.router.url, this.queryParams, 'active-record', recordType);
    } else {
      this.queryParams = this.queryParamsService.deleteQueryString(this.router.url, this.queryParams, 'active-record');
    }
  }

  /**
   * Desmarca la ficha que haya sido seleccionada.
   */
  onBackRecords(): void {
    this.recordSelected = null;
    this.setActiveRecordQueryParam(null);
  }

  /**
   * Establece los datos del paciente
   * @param patient Paciente
   */
  async setPatient(patient: IPatient): Promise<void> {
    this.patient = patient;
    if (!this.patient || this.patient.user) return;
    if (this.patient.user && !this.patient.user.address) {
      this.patient.user.address = {
        city: null,
        country: null,
        cp: null,
        reference: null,
        state: null,
        street: null,
        suburb: null
      };
    }
    if (this.patient.user.gender === GenderEnum.MALE) {
      this.records.forEach(item => {
        if (item.name === 'Mujer') {
          item.hide = true;
          return;
        }
      });
    }
  }

  onChangeLastUpdateRecord(event: IRecordEmit, recordType: RecordTypeEnum): void {
    if (!event?.updated) return;
    this.recordSelected.lastUpdated = new Date(event.record.updatedAt).toISOString();
    const record = this.records.find(_record => _record.uid === recordType);
    if (!record) return;
    record.lastUpdated = cloneDeep(this.recordSelected.lastUpdated);
  }

  ngOnDestroy(): void {
    this.patientSubscription.unsubscribe();
    this.serviceSubscription.unsubscribe();

    // se deben cambiar las fichas para indicar que se deben colapsar
    this.store.dispatch(new RecordOtherHabitsAction.Deploy(false));
    this.store.dispatch(new RecordPhysicalActivityAction.Deploy(false));
    this.store.dispatch(new RecordMedicalPersonalAction.Deploy(false));
    this.store.dispatch(new RecordMedicalInheritedFamilyAction.Deploy(false));
    this.store.dispatch(new RecordNutritionalHistoryAction.Deploy(false));
    this.store.dispatch(new RecordEnvironmentAffectivityAction.Deploy(false));
    this.store.dispatch(new RecordHistoryAttitudeAction.Deploy(false));
    this.store.dispatch(new RecordNutritionalPhysicalExaminationAction.Deploy(false));
    this.store.dispatch(new RecordSleepAction.Deploy(false));
    this.store.dispatch(new RecordBiochemistAction.Deploy(false));
    this.store.dispatch(new RecordHistoryExperienceAction.Deploy(false));
  }
}

